import { useEffect, useState } from 'react'
import { Bell, X } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { A2hsSteps } from './PushSetupCard'
import { canPromptInstall, promptInstall } from '../lib/installPrompt'
import { ensurePushSubscription, needsA2hsForPush } from '../lib/webPush'

const DISMISSED_KEY = 'notify.orderReady.dismissed'
const INSTALL_DISMISSED_KEY = 'push.installGuide.dismissed'

function supportsNotifications(): boolean {
  return typeof Notification !== 'undefined'
}

/**
 * Bottom-sheet ask for notifications on /my-orders. Two modes (#1230):
 *
 * - `permission`: the browser can do notifications now — ask for permission,
 *   then establish a Web Push subscription so the call reaches the customer
 *   even after they close the app.
 * - `install`: iOS Safari (not standalone) can't do notifications at all —
 *   show the Add-to-Home-Screen steps instead. Once opened from the Home
 *   Screen, the prompt comes back in `permission` mode.
 *
 * The in-app banner + vibrate still fire without permission, so dismissing
 * either mode doesn't break the feature.
 */
export default function NotificationPermissionPrompt() {
  const { t, locale } = useI18n()
  const [mode, setMode] = useState<'permission' | 'install' | null>(null)

  useEffect(() => {
    let target: 'permission' | 'install' | null = null
    if (needsA2hsForPush()) {
      if (localStorage.getItem(INSTALL_DISMISSED_KEY) !== 'true') target = 'install'
    } else if (
      supportsNotifications() &&
      Notification.permission === 'default' &&
      localStorage.getItem(DISMISSED_KEY) !== 'true'
    ) {
      target = 'permission'
    }
    if (!target) return
    // Delay slightly so the page paints first.
    const id = window.setTimeout(() => setMode(target), 800)
    return () => window.clearTimeout(id)
  }, [])

  const dismiss = () => {
    localStorage.setItem(mode === 'install' ? INSTALL_DISMISSED_KEY : DISMISSED_KEY, 'true')
    setMode(null)
  }

  const enable = async () => {
    try {
      const result = await Notification.requestPermission()
      if (result === 'granted') {
        // 許可された瞬間に Push 購読も確立 (失敗しても in-app 通知は生きる)
        void ensurePushSubscription(locale)
      }
    } catch {
      // ignore
    }
    dismiss()
  }

  // #1230 Android: beforeinstallprompt が取れていればネイティブのインストール
  // ダイアログを直接出せる (通知はブラウザのままでも届くため任意の導線)
  const installNow = async () => {
    await promptInstall()
    dismiss()
  }

  if (!mode) return null

  return (
    <div className="fixed inset-x-0 bottom-0 z-[95] flex justify-center px-3 pb-[max(1rem,env(safe-area-inset-bottom))] pointer-events-none">
      <div className="pointer-events-auto w-full max-w-[430px] animate-slide-up rounded-2xl border border-gold/40 bg-white shadow-xl">
        <div className="flex items-start gap-3 px-4 pt-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/15">
            <Bell size={20} className="text-gold" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-display text-sm font-bold text-text-primary">
              {t(mode === 'install' ? 'push.installTitle' : 'notify.permissionTitle')}
            </p>
            <p className="mt-1 font-body text-xs text-text-secondary leading-relaxed">
              {t(mode === 'install' ? 'push.installBody' : 'notify.permissionBody')}
            </p>
            {mode === 'install' && <A2hsSteps />}
          </div>
          <button
            onClick={dismiss}
            className="shrink-0 rounded-full p-1 tap-effect hover:bg-gray-100"
            aria-label="close"
          >
            <X size={16} className="text-text-tertiary" />
          </button>
        </div>
        <div className="flex gap-2 px-3 pb-3 pt-3">
          <button
            onClick={dismiss}
            className="flex-1 rounded-lg border border-gray-200 bg-white py-2.5 font-body text-sm text-text-secondary tap-effect hover:bg-gray-50"
          >
            {t('notify.permissionLater')}
          </button>
          {mode === 'permission' && (
            <button
              onClick={enable}
              className="flex-1 rounded-lg bg-navy py-2.5 font-display text-sm font-semibold text-white tap-effect hover:bg-navy-light"
            >
              {t('notify.permissionEnable')}
            </button>
          )}
          {mode === 'permission' && canPromptInstall() && (
            <button
              onClick={installNow}
              className="flex-1 rounded-lg border border-navy/30 bg-white py-2.5 font-display text-sm font-semibold text-navy tap-effect hover:bg-navy/5"
            >
              {t('push.installButton')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
